import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Message {
  id: string;
  text: string;
  sender: 'datawyse' | 'user';
  timestamp: Date;
}

export const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Start the chat sequence
    startChatSequence();
  }, []);

  const startChatSequence = async () => {
    // Clear any existing messages first
    setMessages([]);
    setIsTyping(false);
    setIsAnalyzing(false);

    // Wait a moment after chat appears
    await wait(300);

    // Add DataWise message immediately (no typing animation)
    setMessages([
      {
        id: 'datawyse-1',
        text: 'What would you like to know about the monthly expenditure?',
        sender: 'datawyse',
        timestamp: new Date(),
      },
    ]);

    // Wait briefly then show user message immediately (no typing dots)
    await wait(1000);

    // Add user message immediately (replace entire array to prevent duplication)
    setMessages([
      {
        id: 'datawyse-1',
        text: 'What would you like to know about the monthly expenditure?',
        sender: 'datawyse',
        timestamp: new Date(),
      },
      {
        id: 'user-1',
        text: 'Why is the expense high in the month of May?',
        sender: 'user',
        timestamp: new Date(),
      },
    ]);

    // Wait briefly then show analyzing animation
    await wait(500);
    setIsAnalyzing(true);

    // Show analyzing for 2 seconds
    await wait(2000);
    setIsAnalyzing(false);

    // Add Datawyse response with expense breakdown
    setMessages([
      {
        id: 'datawyse-1',
        text: 'What would you like to know about the monthly expenditure?',
        sender: 'datawyse',
        timestamp: new Date(),
      },
      {
        id: 'user-1',
        text: 'Why is the expense high in the month of May?',
        sender: 'user',
        timestamp: new Date(),
      },
      {
        id: 'datawyse-2',
        text: 'Looks like items:\n• **Event Sponsorship** - Marketers Hub: **$150K**\n• **Booth design** vendor: **$20K**\n\ncontributed to the **$170K overspend** in May 2025.',
        sender: 'datawyse',
        timestamp: new Date(),
      },
    ]);

    // Wait 7 seconds then complete
    await wait(7000);

    // Animation complete - let parent control the loop
  };

  // Removed simulateTyping function - now using simple dots indicator

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      // Directly set scrollTop to scrollHeight for local scroll only
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const chatContainerVariants = {
    hidden: {
      opacity: 0,
      y: 0,
      scale: 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: 'easeInOut',
        staggerChildren: 0.1,
      },
    },
  };

  const messageVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
  };

  const typingIndicatorVariants = {
    typing: {
      scale: [1, 1.1, 1],
      transition: {
        duration: 1,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <motion.div
      className="relative z-20 h-[265px] flex-1 max-[599px]:h-[180px]"
      variants={chatContainerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Chat Card Container */}
      <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-3 shadow-lg max-[599px]:p-2">
        {/* Messages Container */}
        <div
          ref={messagesContainerRef}
          className="scrollbar-hide flex-1 space-y-2 overflow-x-hidden overflow-y-auto rounded-xl border border-gray-100 bg-white p-3 max-[599px]:space-y-1 max-[599px]:p-2"
          style={{ overscrollBehaviorY: 'contain' }}
        >
          <AnimatePresence>
            {messages.map(message => (
              <motion.div
                key={message.id}
                variants={messageVariants}
                initial="hidden"
                animate="visible"
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`${
                    message.sender === 'user'
                      ? 'max-w-[220px] max-[599px]:max-w-[160px]'
                      : 'max-w-[280px] max-[599px]:max-w-[200px]'
                  } rounded-xl px-3 py-2 max-[599px]:px-2 max-[599px]:py-1.5 ${
                    message.sender === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-200 bg-white text-gray-800 shadow-sm'
                  }`}
                >
                  <p
                    className="text-xs leading-relaxed whitespace-pre-line max-[599px]:text-[10px] max-[599px]:leading-tight"
                    dangerouslySetInnerHTML={{
                      __html: message.text.replace(
                        /\*\*(.*?)\*\*/g,
                        '<strong>$1</strong>'
                      ),
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing Indicator */}
          <AnimatePresence>
            {isTyping && (
              <motion.div
                className="flex justify-start"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="max-w-[280px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm max-[599px]:max-w-[200px] max-[599px]:px-2 max-[599px]:py-1.5">
                  <div className="flex items-center space-x-1">
                    <motion.div
                      className="flex space-x-1"
                      variants={typingIndicatorVariants}
                      animate="typing"
                    >
                      <div className="h-1.5 w-1.5 rounded-full bg-gray-400 max-[599px]:h-1 max-[599px]:w-1"></div>
                      <div className="h-1.5 w-1.5 rounded-full bg-gray-400 opacity-75 max-[599px]:h-1 max-[599px]:w-1"></div>
                      <div className="h-1.5 w-1.5 rounded-full bg-gray-400 opacity-50 max-[599px]:h-1 max-[599px]:w-1"></div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Analyzing Indicator */}
          <AnimatePresence>
            {isAnalyzing && (
              <motion.div
                className="flex justify-start"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="max-w-[280px] rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm max-[599px]:max-w-[200px] max-[599px]:px-2 max-[599px]:py-1.5">
                  <div className="flex items-center space-x-2 max-[599px]:space-x-1">
                    <span className="text-xs text-gray-700 max-[599px]:text-[10px]">
                      Analyzing
                    </span>
                    <motion.div
                      className="flex space-x-1"
                      variants={typingIndicatorVariants}
                      animate="typing"
                    >
                      <div className="h-1.5 w-1.5 rounded-full bg-blue-500 max-[599px]:h-1 max-[599px]:w-1"></div>
                      <div className="h-1.5 w-1.5 rounded-full bg-blue-500 opacity-75 max-[599px]:h-1 max-[599px]:w-1"></div>
                      <div className="h-1.5 w-1.5 rounded-full bg-blue-500 opacity-50 max-[599px]:h-1 max-[599px]:w-1"></div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Invisible div for scrolling to bottom */}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </motion.div>
  );
};
